"use client";

import SignInForm from "@/components/common/signin-form";
import SignUpForm from "@/components/common/signup-form";
import SocialLoginButton from "@/components/shared/social-login-button";
import { cn } from "@/lib/utils";
import { Tab } from "@headlessui/react";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { toast } from "react-hot-toast";

type AuthFormProps = {
	onSuccess?: () => void;
	title?: React.ReactNode;
	defaultTab?: number; // 0 for signup, 1 for signin
	className?: string;
};

const providers = [
	{ provider: "google", imageUrl: "/images/auth-providers/google.png" },
	{ provider: "linkedin", imageUrl: "/images/auth-providers/linkedin.png" },
	{ provider: "twitter", imageUrl: "/images/auth-providers/x.png" },
	// { provider: "apple", imageUrl: "/images/auth-providers/apple.png" },
];

function AuthForm({ onSuccess, title, defaultTab = 0, className }: AuthFormProps) {
	const [activeTab, setActiveTab] = useState(defaultTab);
	const [preFilledEmail, setPreFilledEmail] = useState<string>("");

	const handleSocialLogin = (provider: string) => {
		signIn(provider);
	};

	const handleSignUpSuccess = () => {
		onSuccess?.();
	};

	const handleUserExists = (email: string) => {
		// Show a helpful message to the user
		toast.success("Account found! Switching to sign in and sending OTP to your email.");

		// Store the email to prefill in signin form
		setPreFilledEmail(email);

		// Switch to signin tab
		setActiveTab(1);
	};

	return (
		<div className={cn("w-full", className)}>
			{title && <div className="mb-4">{title}</div>}

			<Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
				<Tab.List className="flex space-x-1 rounded-lg bg-brand-primary p-1">
					<Tab
						className={({ selected }) =>
							cn(
								"w-full rounded-lg py-2.5 text-sm font-medium leading-5",
								selected ? "bg-white text-brand-primary" : "text-white"
							)
						}
					>
						Sign Up
					</Tab>
					<Tab
						className={({ selected }) =>
							cn(
								"w-full rounded-lg py-2.5 text-sm font-medium leading-5",
								selected ? "bg-white text-brand-primary" : "text-white"
							)
						}
					>
						Sign In
					</Tab>
				</Tab.List>
				<Tab.Panels className="mt-4">
					<Tab.Panel>
						<SignUpForm onSuccess={handleSignUpSuccess} onUserExists={handleUserExists} />

						<div className="space-y-2 pt-2">
							<div className="flex items-center gap-2.5">
								<p className="whitespace-nowrap text-sm text-gray-600">
									or <span className="font-semibold">Sign up using</span>
								</p>
								<div className="flex-1 border-t border-gray-300"></div>
							</div>

							<div className="flex gap-3">
								{providers.map((item) => (
									<SocialLoginButton
										key={item.provider}
										provider={item.provider}
										imageUrl={item.imageUrl}
										onClick={() => handleSocialLogin(item.provider)}
									/>
								))}
							</div>
						</div>
					</Tab.Panel>
					<Tab.Panel>
						<SignInForm
							preFilledEmail={preFilledEmail}
							onEmailUsed={() => setPreFilledEmail("")} // Clear prefilled email after use
						/>

						<div className="space-y-2 pt-2">
							<div className="flex items-center gap-2.5">
								<p className="whitespace-nowrap text-sm text-gray-600">
									or <span className="font-semibold">Sign in using</span>
								</p>
								<div className="flex-1 border-t border-gray-300"></div>
							</div>

							<div className="flex gap-3">
								{providers.map((item) => (
									<SocialLoginButton
										key={item.provider}
										provider={item.provider}
										imageUrl={item.imageUrl}
										onClick={() => handleSocialLogin(item.provider)}
									/>
								))}
							</div>
						</div>
					</Tab.Panel>
				</Tab.Panels>
			</Tab.Group>
		</div>
	);
}

export default AuthForm;
